import { useTranslation } from 'react-i18next';
import { InputTable } from '../../assets/styles/InputTable';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import { useOutletContext } from 'react-router-dom';
import useFirstItemAtTop from '../../hooks/useFirstItemAtTop';
import useTravelWeather from '../../hooks/useTravelWeather';
import { getWeatherIcon } from '../../utils/weatherUtils';
import { userState } from '../../atoms';

const Root = styled.div`
  user-select: none;
  height: 100%;
  padding: 1.5rem;

  > .widget-header {
    width: 100%;
    padding: 0 0 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-bottom: 1px solid var(--outline);

    &.margin-top {
      margin-bottom: 0;
    }
    > .widget-title {
      align-items: center;

      span {
        font-size: var(--widget-font-size-lg);
        color: var(--text-primary);

        span.badge {
          font-size: inherit;
          color: var(--widget-primary);
        }
      }
    }
  }

  .widget-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    /* max-height: 400px; 원하는 최대 높이 설정 */
    overflow: hidden; /* 세로 스크롤 비활성화 */
  }

  .widget-body.table {
    width: 100%;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    > div {
      height: 100%;
      .table {
        flex-grow: 1;
      }
    }
    .tr {
      pointer-events: none;
      font-size: 0.875rem;

      .padding-right {
        padding-right: 1rem;
      }
      .tableStickyNo {
        padding-left: 1rem;
        font-weight: 600;
      }
      span.greenLight {
        width: 2.125rem;
        height: 1.5rem;
        color: var(--filled-green);
        font-weight: 600;
        text-align: center;
        border-radius: 0.25rem;
        background-color: var(--tonal-green);
      }
      > div {
        min-height: fit-content;
        word-break: break-all;
        color: var(--text-tertiary);
      }
    }
  }

  .trip-list-wrap {
    display: flex;
    flex-direction: column;
    // gap: 0.5rem;
    width: 100%;
    height: 100%;
    padding: 0;
  }

  .trip-item {
    display: flex;
    flex-direction: column;
    width: 100%;
    border-bottom: 1px solid var(--outline);
    // border-radius: 0.5rem;
    padding: 1.5rem 0;
    gap: 0.75rem;

    .trip-item-header {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .trip-time-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .trip-weather {
        display: flex;
        align-items: center;
        line-height: 0;

        svg {
          display: block;
        }
      }

      .time {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        // border: 1px solid var(--primary);
        background-color: var(--widget-primary);
        color: #fff;
        width: max-content;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-size: 1.25rem;
        line-height: 1.2;

        &.all-day {
          color: #101010;
          background-color: var(--widget-secondary);
        }

        span {
          font-variation-settings: 'FILL' 0;
          font-size: 1rem;
        }
      }

      .trip-item-name {
        display: flex;
        align-items: center;
        gap: 0.375rem;

        span.material-symbols-rounded {
          // padding-top: 3px;
          font-size: 20px;
          color: var(--text-primary);
        }

        span.trip-item-tit {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          text-overflow: ellipsis;
          overflow: hidden;
          // font-size: var(--widget-font-size-lg);
          font-size: 32px;
          font-weight: 700;
          line-height: 1.4;
          max-height: 90px;
          height: 100%;
          color: var(--text-primary);
        }
      }
    }

    .trip-item-bottom {
      display: flex;
      flex-direction: column;
      font-size: 1.5rem;
      color: var(--text-quaternary);
      gap: 0.25rem;

      .trip-car {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .trip-car-name-cont {
          display: flex;
          flex-direction: column;

          span {
            line-height: 1.4;

            b {
              font-weight: 600;
            }
          }
        }
      }

      .trip-driver {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        span {
          line-height: 1.4;

          b {
            font-weight: 600;
          }
        }
      }
    }

    span.material-symbols-rounded {
      font-variation-settings: 'FILL' 1;
      font-size: 1.25rem;
    }
  }

  .noData {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    height: 100%;
    color: var(--text-primary);
    font-size: var(--widget-font-size-lg);
    font-weight: 400;

    span {
      color: var(--widget-sub-text);
      font-size: var(--widget-font-size-5xl);

      &.material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
      }
    }
  }

  @keyframes tripScrollUp {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-50%);
    }
  }
`;

type Props = {
  name: string;
  autoRefresh: boolean;
};

// const TABLE_HEADER_HEIGHT = 49;

// 동행자 목록에서 운전자명 제거
const removeDriverFromUserList = (driver: string, userList: string) => {
  if (!userList) return '';
  const userArray = userList.split('|');
  const filteredArr = userArray.filter((item) => item !== driver);
  const result = filteredArr.join(', ');
  return ` / ${result}`;
};

// 출장 현황
export const BusinessTripInfo = ({ name, autoRefresh }: Props) => {
  const isSiteDashboard = autoRefresh; // 상황판 여부
  const { t } = useTranslation();
  const tbodyRef = useRef<HTMLDivElement>(null);
  const widgetBodyRef = useRef<HTMLDivElement>(null);
  const parentBodyRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const firstItemRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const [isScrollEnabled, setIsScrollEnabled] = useState(false);
  const { data, isLoading, isError, refetch } = GetFetchStatus({ path: '/schedule/travel/board', hCdOnly: { value: true, key: 'cCd' }, refetchInterval: 30 * 60 * 1000 });
  const size = useOutletContext<any>();
  const isAtTop = useFirstItemAtTop(parentBodyRef, firstItemRef, data);

  // 출장 현장 기상 정보 위젯(094)과 동일한 캐시를 사용 ( 두 위젯이 함께 배치되어도 API는 1회만 호출 )
  const userInfo = useRecoilValue(userState);
  const { data: travelWeather } = useTravelWeather({ cCd: userInfo.hCd, refetchInterval: 30 * 60 * 1000 });

  // 출장 일정 ↔ 기상 정보 매칭용 맵 ( 두 API 모두 일정 고유키인 smIdx를 내려줌 )
  const weatherMap = useMemo(() => {
    const map = new Map<string, any>();
    (travelWeather?.oriArr ?? []).forEach((el: any) => {
      if (el.smIdx !== undefined && el.smIdx !== null) map.set(String(el.smIdx), el.weather);
    });
    return map;
  }, [travelWeather?.oriArr]);

  // 출장 건에 해당하는 날씨 아이콘 ( 날씨 정보가 없으면 미노출 )
  const renderWeatherIcon = (el: any) => {
    const weather = weatherMap.get(String(el.smIdx));
    if (!weather || weather.errMessage || !weather.todaySky || weather.todaySky === '-') return null;

    return <div className='trip-weather'>{getWeatherIcon(weather.todaySky, 40)}</div>;
  };

  // isAtTop이 true일 때 3초간 스크롤을 비활성화하고 이후 다시 활성화하는 로직
  /*
  useEffect(() => {
    if (isScrollEnabled && isAtTop) {
      // isAtTop이 true가 되면 스크롤을 즉시 비활성화
      setIsScrollEnabled(false);
      const timer = setTimeout(() => {
        setIsScrollEnabled(true); // 3초 후에 스크롤을 다시 활성화
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {}; // isAtTop이 false일 때 빈 cleanup 함수 반환
  }, [isAtTop]);
  */

  const scroll = (widgetBody: HTMLDivElement | null, tbody: HTMLDivElement | null, filteredCount: number, scrollEnabled: boolean): boolean => {
    if (filteredCount < 2 || !widgetBody || !tbody) return false;
    const singleCopyHeight = scrollEnabled ? tbody.scrollHeight / 2 : tbody.scrollHeight;
    return singleCopyHeight > widgetBody.clientHeight;
  };

  const onClickRefresh = () => {
    refetch();
  };

  useEffect(() => {
    // 높이 비교해서 스크롤여부
    if (widgetBodyRef.current && tbodyRef.current) {
      const filteredCount = data.filter((el2: any) => el2.completeYn !== 'Y').length;
      const result = scroll(widgetBodyRef.current, tbodyRef.current, filteredCount, isScrollEnabled);
      setIsScrollEnabled(result);
    }
  }, [data, tbodyRef, widgetBodyRef, size]); // data가 변경될 때마다 높이 비교

  const loading = !data || isLoading;
  if (isError) return <IssueGuide />;
  return (
    <Root>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>
            {t(name)}
            <span className='badge'>{data?.filter((el2: any) => el2.completeYn !== 'Y')?.length ?? 0}</span>
          </span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='widget-body table' ref={widgetBodyRef} style={{ display: 'block' }}>
          {data?.filter((el2: any) => el2.completeYn !== 'Y').length > 0 ? (
            <div className='trip-cont' ref={parentBodyRef} style={{ height: 'auto' }}>
              <div
                className='trip-list-wrap'
                ref={tbodyRef}
                style={{ animation: `${isScrollEnabled ? `tripScrollUp ${data.filter((el2: any) => el2.completeYn !== 'Y').length * 10}s linear infinite` : 'none'}` }}
              >
                {[...data, ...(isScrollEnabled ? data : [])]
                  .filter((el2: any) => el2.completeYn !== 'Y')
                  .map((el: any, i: number) => (
                    <div key={`equipInfo_${i}`} className='trip-item' ref={i === 0 ? firstItemRef : null}>
                      <div className='trip-item-header'>
                        <div className='trip-time-row'>
                          <div className={`time ${el.alldayYn === 'Y' ? 'all-day' : ''}`}>
                            <span className='material-symbols-rounded'>schedule</span>
                            {el.alldayYn === 'Y' ? t('종일') : el.sTime}
                          </div>
                          {renderWeatherIcon(el)}
                        </div>

                        <div className='trip-item-name'>
                          <span className='material-symbols-rounded'>distance</span>
                          <span className='trip-item-tit'>
                            {el.hName} / {el.sName}
                          </span>
                        </div>
                      </div>
                      <div className='trip-item-bottom'>
                        <div className='trip-car'>
                          <span className='material-symbols-rounded'>directions_car</span>
                          <div className='trip-car-name-cont'>
                            <span>
                              <b>{el.ccName || '-'}</b> / {el.ccNum || '-'}
                            </span>
                          </div>
                        </div>
                        <div className='trip-driver'>
                          <span className='material-symbols-rounded'>person</span>
                          <span>
                            {el.driver ? (
                              <>
                                <b>{el.driver}</b>(운전자)
                              </>
                            ) : (
                              '없음'
                            )}
                            {el.userName && el.userName !== el.driver && removeDriverFromUserList(el.driver, el.userName)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className='noData'>
              <span className='material-symbols-rounded'>person_off</span>
              {t('금일 출장이 없습니다.')}
            </div>
          )}

          {/* <InputTable>
            <div className='thead'>
              <div className='tr'>
                <div className='trCol4 flex-center text_tertiary'>{t('시간')}</div>
                <div className='trCol6 flex-center text_tertiary'>{t('본사 / 현장')}</div>
                <div className='trCol5 flex-center text_tertiary content-overflow'>{t('운전자 / 동행자')}</div>
                <div className='trCol6 flex-center text_tertiary padding-right'>{t('차량')}</div>
              </div>
            </div>
            <div className='table' ref={parentBodyRef}>
              <div
                className='tbody'
                ref={tbodyRef}
                style={{
                  animation: `${isScrollEnabled ? `slide ${data.filter((el2: any) => el2.completeYn !== 'Y').length * 5}s linear infinite` : 'none'}`,
                }}
              >
                {data?.filter((el2: any) => el2.completeYn !== 'Y').length > 0 ? (
                  data
                    .filter((el2: any) => el2.completeYn !== 'Y')
                    .map((el: any, i: number) => (
                      <div className='tr' key={`equipInfo_${i}`} ref={i === 0 ? firstItemRef : null}>
                        <div className='trCol4 flex-center flex-col'>
                          <span>{el.alldayYn === 'Y' ? t('종일') : el.sTime}</span>
                          {el.completeYn === 'Y' && <span className='greenLight'>{t('완료')}</span>}
                        </div>
                        <div className='trCol6 flex-center flex-col'>
                          <span>{el.hName}</span>
                          <span>{el.sName}</span>
                        </div>
                        <div className='trCol5 flex-center content-overflow flex-col'>
                          <span>{el.driver}</span>
                          <span>{removeDriverFromUserList(el.driver, el.userName)}</span>
                        </div>
                        <div className='trCol6 flex-center padding-right flex-col'>
                          <span>{el.ccName}</span>
                          <span>{el.ccNum}</span>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
              </div>
            </div>
          </InputTable> */}
        </div>
      )}
    </Root>
  );
};
